import { getTenantI18nConfigCache } from '@/utils/i18n';

/**
 * 后端与前端统一使用 YYYY-MM-DD HH:mm:ss 字符串。
 * 约定：后端返回/接收的字符串按 UTC 理解，页面展示/选择的字符串按后端配置的 IANA timeZone 理解。
 */
type DateLike = string | Date | number | null | undefined;

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const DEFAULT_TIME_ZONE = 'Asia/Shanghai';
const DATE_TIME_REG = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

let appTimeZone = DEFAULT_TIME_ZONE;

/**
 * 设置后端返回的 IANA 时区，例如 Asia/Shanghai、America/Los_Angeles
 */
export function setAppTimeZone(timeZone?: string): void {
  appTimeZone = isValidTimeZone(timeZone) ? timeZone! : DEFAULT_TIME_ZONE;
}

export function getAppTimeZone(): string {
  return getEffectiveTimeZone();
}

/**
 * 后端 UTC 时间字符串 → 当前系统配置时区展示时间
 * @param utcStr 后端时间，例：2026-04-07 10:00:00 或 2026-04-07T10:00:00Z
 * @returns 展示时间，例：2026-04-07 18:00:00（Asia/Shanghai）
 */
export function utcToCurrentLocalTime(utcStr?: string | null): string {
  const value = utcStr?.trim();
  if (!value) return '—';

  const date = parseUtcDate(value);
  if (isNaN(date.getTime())) return '';

  return formatInTimeZone(date, getEffectiveTimeZone());
}

/**
 * 后端 UTC 时间字符串 → 当前系统配置时区 Date 对象（适配 el-date-picker 绑定）
 */
export function utcToCurrentLocalDate(utcStr: string): Date {
  const viewTime = utcToCurrentLocalTime(utcStr);
  const date = new Date(viewTime.replace(' ', 'T'));

  if (isNaN(date.getTime())) {
    throw new Error('无效的UTC时间格式');
  }

  return date;
}

/**
 * 页面当前系统配置时区时间 → 后端 UTC 时间字符串
 * @returns YYYY-MM-DD HH:mm:ss
 */
export function toUtcIso(localDateTime: DateLike): string {
  const value = normalizeDateLike(localDateTime);
  if (!value) return '_';

  const date = zonedTimeToUtc(value, getEffectiveTimeZone());

  if (isNaN(date.getTime())) return '_';

  return formatUtc(date);
}

export const serverTimeToViewTime = utcToCurrentLocalTime;
export const viewTimeToServerTime = toUtcIso;

function getEffectiveTimeZone(): string {
  const cachedTimeZone = getTenantI18nConfigCache()?.time?.timeZone;

  if (isValidTimeZone(cachedTimeZone)) return cachedTimeZone;
  if (isValidTimeZone(appTimeZone)) return appTimeZone;

  return DEFAULT_TIME_ZONE;
}

function parseUtcDate(value: string): Date {
  const match = value.match(DATE_TIME_REG);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)));
  }

  return new Date(value);
}

function normalizeDateLike(value: DateLike): string {
  if (value === null || value === undefined || value === '') return '';

  if (value instanceof Date) {
    return formatLocalDate(value);
  }

  if (typeof value === 'number') {
    return formatLocalDate(new Date(value));
  }

  return value;
}

function zonedTimeToUtc(value: string, timeZone: string): Date {
  const parts = parseDateTimeParts(value);
  const localTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  const offset = getTimeZoneOffset(new Date(localTimestamp), timeZone);
  let utcTimestamp = localTimestamp - offset;

  const adjustedOffset = getTimeZoneOffset(new Date(utcTimestamp), timeZone);
  if (offset !== adjustedOffset) {
    utcTimestamp = localTimestamp - adjustedOffset;
  }

  return new Date(utcTimestamp);
}

function getTimeZoneOffset(date: Date, timeZone: string): number {
  const parts = getTimeZoneParts(date, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

  return asUtc - date.getTime();
}

function formatInTimeZone(date: Date, timeZone: string): string {
  const parts = getTimeZoneParts(date, timeZone);
  return formatParts(parts);
}

function formatUtc(date: Date): string {
  return formatParts({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
  });
}

function formatLocalDate(date: Date): string {
  return formatParts({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  });
}

function getTimeZoneParts(date: Date, timeZone: string): DateTimeParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const partMap = formatter.formatToParts(date).reduce<Record<string, string>>((map, part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
    return map;
  }, {});

  return {
    year: Number(partMap.year),
    month: Number(partMap.month),
    day: Number(partMap.day),
    hour: Number(partMap.hour),
    minute: Number(partMap.minute),
    second: Number(partMap.second),
  };
}

function parseDateTimeParts(value: string): DateTimeParts {
  const match = value?.match(DATE_TIME_REG);
  if (!match) {
    return {
      year: 1970,
      month: 1,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
    };
  }

  const [, year, month, day, hour, minute, second] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
}

function formatParts(parts: DateTimeParts): string {
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function isValidTimeZone(timeZone?: string): boolean {
  if (!timeZone) return false;

  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}
